/**
 * CallbackFilters — controlled filter panel for the callbacks list page
 * (Story 7.13b). Parent owns the `value`/`onChange` so it can serialize
 * into the URL/querystring; this component is purely presentational.
 *
 * Filters:
 *   - status multi-select (chip toggles)
 *   - campaign id (text input — UUID)
 *   - customer phone substring
 *   - scheduled-from / scheduled-to (datetime-local)
 */
import { type ChangeEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import {
  ALL_CALLBACK_STATUSES,
  type CallbackExecutionStatus,
  type CallbackListFilters,
} from '@/lib/callbacks';

export interface CallbackFiltersProps {
  value: CallbackListFilters;
  onChange: (next: CallbackListFilters) => void;
  onClear: () => void;
}

export function CallbackFilters({ value, onChange, onClear }: CallbackFiltersProps) {
  const hasAnyFilter =
    value.statuses.length > 0 ||
    value.campaignId.length > 0 ||
    value.customerPhone.length > 0 ||
    value.scheduledFrom.length > 0 ||
    value.scheduledTo.length > 0;

  function toggleStatus(s: CallbackExecutionStatus) {
    const next = value.statuses.includes(s)
      ? value.statuses.filter((x) => x !== s)
      : [...value.statuses, s];
    onChange({ ...value, statuses: next });
  }

  function setText(key: keyof CallbackListFilters) {
    return (e: ChangeEvent<HTMLInputElement>) => onChange({ ...value, [key]: e.target.value });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Status filter">
        {ALL_CALLBACK_STATUSES.map((s) => {
          const active = value.statuses.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                active
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-background text-muted-foreground hover:bg-accent'
              }`}
              aria-pressed={active}
              data-testid={`callback-filter-status-${s}`}
            >
              {s}
            </button>
          );
        })}
        {hasAnyFilter ? (
          <Button size="sm" variant="ghost" onClick={onClear} data-testid="callback-filters-clear">
            Limpar
          </Button>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="space-y-1">
          <Label htmlFor="f-campaign">Campanha (UUID)</Label>
          <Input
            id="f-campaign"
            value={value.campaignId}
            onChange={setText('campaignId')}
            placeholder="opcional"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-phone">Telefone</Label>
          <Input
            id="f-phone"
            value={value.customerPhone}
            onChange={setText('customerPhone')}
            placeholder="substring dos dígitos"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-from">Agendado de</Label>
          <Input
            id="f-from"
            type="datetime-local"
            value={value.scheduledFrom}
            onChange={setText('scheduledFrom')}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-to">Agendado até</Label>
          <Input
            id="f-to"
            type="datetime-local"
            value={value.scheduledTo}
            onChange={setText('scheduledTo')}
          />
        </div>
      </div>
    </div>
  );
}
